import { useRef, useState } from 'react'
import { Paperclip, Upload, X } from 'lucide-react'
import { cn } from '../../lib/cn.js'
import { readableSize, uploadTo } from '../../lib/upload.js'
import { Button } from './Button.js'
import { Text } from './Typography.js'
import { Stack, Row } from '../layout/Stack.js'
import { StatusBanner } from '../feedback/StatusBanner.js'

export interface FileUploadProps {
  /** The admin route that claims a slot — `/v1/admin/masterclasses/materials/upload`. */
  claimUrl: string
  /**
   * What the claim route wants beside the type and the size.
   *
   * The same prop `ImageUpload` has, and for the same reason: the routes do not
   * agree. `POST /v1/admin/masterclasses/materials/upload` takes neither a kind nor
   * an alt text; `POST /v1/provider/uploads` **requires** `kind` and refuses the
   * request without it. Without this the professional's documents step claimed a
   * slot with no kind and every upload was refused at the first step — the caller
   * names its route's shape rather than this component guessing at one of them.
   */
  claimBody?: Record<string, unknown>
  /** Called once the bytes have landed and the asset exists. */
  onUploaded: (assetId: string, file: File) => void
  /** Stated **before** the picker opens, which is the product's rule for this. */
  accept?: string
  maxBytes: number
  label?: string
  disabled?: boolean
  id?: string
}

/**
 * A file, chosen and sent.
 *
 * The limits are on the screen before the picker opens rather than in the error
 * afterwards — 3.7's rule, and it applies to a worksheet as much as to a
 * photograph. Dropping is supported because a desk has a folder open beside it.
 *
 * The size is checked here *and* signed into the upload URL, so a browser that
 * skips this check is refused by the store rather than by nothing.
 */
export function FileUpload({
  claimUrl,
  claimBody,
  onUploaded,
  accept,
  maxBytes,
  label = 'Add a file',
  disabled,
  id,
}: FileUploadProps) {
  const input = useRef<HTMLInputElement>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [over, setOver] = useState(false)

  const take = async (file: File | undefined) => {
    if (!file) return
    setError(null)
    if (file.size > maxBytes) {
      setError(
        `That file is ${readableSize(file.size)}. The most this takes is ${readableSize(maxBytes)}.`,
      )
      return
    }
    setBusy(true)
    try {
      const { assetId } = await uploadTo(claimUrl, file, claimBody)
      onUploaded(assetId, file)
    } catch (e) {
      setError((e as Error).message)
    } finally {
      setBusy(false)
      if (input.current) input.current.value = ''
    }
  }

  return (
    <Stack gap={2} className="@container">
      <div
        onDragOver={(e) => {
          e.preventDefault()
          setOver(true)
        }}
        onDragLeave={() => setOver(false)}
        onDrop={(e) => {
          e.preventDefault()
          setOver(false)
          void take(e.dataTransfer.files[0])
        }}
        className={cn(
          /* One row from `sm` up, the same shape as `ImageUpload`. It was a
             centred column six items tall with `py-8`, which is 150px of
             empty box sitting in the middle of a form — and the two pickers
             on the class form did not even look like the same product.

             Below `sm` the row itself stays — the icon beside the words,
             which is the pair that reads fine at any width — but the button
             drops to its own full-width line underneath. Squeezed into the
             same row as the icon and the wrapping text, a phone had perhaps
             140px left for "Drop a file here, or choose one", which wrapped
             it to three or four cramped lines beside a button with room to
             spare. Its own line gives the text the width back and gives the
             button a touch target as wide as the one everything else on this
             form gets. */
          /* By the width it is given, not the window's (client, 2026-10-02): inside a
             narrow card on a wide screen the row squeezed the words beside the button. */
          'flex flex-col gap-3 rounded-md border border-dashed p-4 transition-colors @md:flex-row @md:items-center @md:gap-4 @md:p-3',
          over ? 'border-coral-fill bg-coral-soft' : 'border-hairline-strong bg-sunk',
          disabled && 'opacity-50 pointer-events-none',
        )}
      >
        <div className="flex min-w-0 flex-1 items-center gap-4">
          <div className="bg-surface border-hairline flex size-11 shrink-0 items-center justify-center rounded-sm border">
            <Paperclip className="text-ink-muted size-5" aria-hidden />
          </div>
          <div className="flex min-w-0 flex-1 flex-col gap-0.5">
            <Text as="span" variant="body">
              Drop a file here, or choose one
            </Text>
            {/* The types in words: it printed the raw list, "APPLICATION/PDF,IMAGE/JPEG,…",
                which ran under the button (client, 2026-10-02). */}
            <Text as="span" variant="caption" className="break-words">
              {accept ? `${typesInWords(accept)} · ` : ''}up to {readableSize(maxBytes)}
            </Text>
          </div>
        </div>
        <Button
          variant="secondary"
          size="sm"
          className="w-full shrink-0 @md:w-auto"
          loading={busy}
          leadingIcon={<Upload />}
          onClick={() => input.current?.click()}
        >
          {label}
        </Button>
        <input
          ref={input}
          id={id}
          type="file"
          accept={accept}
          className="hidden"
          onChange={(e) => void take(e.target.files?.[0])}
        />
      </div>
      {error && <StatusBanner tone="error">{error}</StatusBanner>}
    </Stack>
  )
}

export interface AttachedFileProps {
  name: string
  detail?: string
  /**
   * Where it actually is.
   *
   * Without one this was a filename and a paperclip: an administrator could
   * attach a worksheet or a link to a class and never find out whether it
   * pointed anywhere. The caller supplies it — a link carries its own
   * address, a file needs one signing, and neither is this component's
   * business.
   */
  href?: string | null
  onRemove?: () => void
}

/** A file already attached, in a list under the picker. */
export function AttachedFile({ name, detail, href, onRemove }: AttachedFileProps) {
  const label = (
    <>
      <Text as="span" variant="body-strong" className="truncate">
        {name}
      </Text>
      {detail && (
        <Text as="span" variant="caption">
          {detail}
        </Text>
      )}
    </>
  )
  return (
    <Row justify="between" className="rounded-md border border-hairline bg-surface px-4 py-3">
      <Row gap={3} className="min-w-0">
        <Paperclip className="size-4 shrink-0 text-ink-muted" aria-hidden />
        {href ? (
          /* A new tab, because opening a worksheet should not take somebody
             out of a half-filled editor. */
          <a
            href={href}
            target="_blank"
            rel="noreferrer"
            className="text-coral-text flex min-w-0 flex-col rounded-sm underline-offset-2 outline-none hover:underline focus-visible:ring-2 focus-visible:ring-coral-fill/40"
          >
            {label}
          </a>
        ) : (
          <span className="flex min-w-0 flex-col">{label}</span>
        )}
      </Row>
      {onRemove && (
        <Button variant="ghost" size="icon-sm" aria-label={`Remove ${name}`} onClick={onRemove}>
          <X />
        </Button>
      )}
    </Row>
  )
}

/** "application/pdf,image/jpeg,.png" as a person says it: "PDF, JPG or PNG". */
function typesInWords(accept: string): string {
  const NAMES: Record<string, string> = {
    'application/pdf': 'PDF', 'image/jpeg': 'JPG', 'image/jpg': 'JPG', 'image/png': 'PNG',
    'image/webp': 'WEBP', 'image/heic': 'HEIC', 'image/gif': 'GIF', 'text/csv': 'CSV',
  }
  const words = [
    ...new Set(
      accept
        .split(',')
        .map((a) => a.trim().toLowerCase())
        .filter(Boolean)
        .map((a) => NAMES[a] ?? (a.startsWith('.') ? a.slice(1).toUpperCase() : a.split('/').pop()!.toUpperCase())),
    ),
  ]
  return words.length > 1 ? `${words.slice(0, -1).join(', ')} or ${words.at(-1)}` : (words[0] ?? '')
}

